import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { readErrorCode } from 'src/services/apis/errors';

// main.tsx extends these at boot; repeated here so tests (which never load main.tsx) get them too.
dayjs.extend(utc);
dayjs.extend(timezone);

const KNOWN = new Set([
  'ALREADY_CLOCKED_IN',
  'NOT_CLOCKED_IN',
  'ALREADY_ON_BREAK',
  'NOT_ON_BREAK',
  'NOT_HOURLY_EMPLOYEE',
  'TIMESHEET_LOCKED',
  'OPEN_ENTRY',
  'CANNOT_REOPEN_PAID',
  'PIN_INVALID',
  'PIN_LOCKED',
  'PIN_NOT_SET',
  'PIN_FORMAT',
  'PERIOD_NOT_ENDED',
  'EXPENSE_CATEGORY_REQUIRED',
  'ENTRY_OVERLAP',
  'BREAK_INVALID',
  'NOT_APPROVED',
  'KIOSK_NEEDS_BRANCH',
  'HOLIDAY_EXISTS',
  'HOLIDAY_YEAR_UNAVAILABLE',
]);

export function formatMinutes(min: number): string {
  return `${Math.floor(min / 60)}h${String(min % 60).padStart(2, '0')}`;
}

/** Times are shown in the RESTAURANT's zone: a manager abroad must see what the staff saw. */
export function localTime(iso: string, tz: string): string {
  return dayjs(iso).tz(tz).format('HH:mm');
}

export function toIsoFromLocal(date: string, time: string, tz: string): string {
  return dayjs.tz(`${date} ${time}`, 'YYYY-MM-DD HH:mm', tz).toISOString();
}

/**
 * Anchors a manager-entered break to its entry instead of a fixed calendar day, so a break
 * taken after midnight on an overnight shift (clock-in 22:00, break 00:30) doesn't compute as
 * before clock-in. `start` rolls forward a day if it would land before `entryClockInIso`; `end`
 * then rolls forward a day off `start`'s own date if it isn't after `start`.
 */
export function breakSpan(entryClockInIso: string, from: string, to: string, tz: string) {
  const entryDate = dayjs(entryClockInIso).tz(tz).format('YYYY-MM-DD');
  let start = toIsoFromLocal(entryDate, from, tz);
  if (start < entryClockInIso) start = toIsoFromLocal(dayjs(entryDate).add(1, 'day').format('YYYY-MM-DD'), from, tz);
  const startDate = dayjs(start).tz(tz).format('YYYY-MM-DD');
  let end = toIsoFromLocal(startDate, to, tz);
  if (end <= start) end = toIsoFromLocal(dayjs(startDate).add(1, 'day').format('YYYY-MM-DD'), to, tz);
  return { start, end };
}

export function timesheetErrorKey(error: unknown): string {
  const code = readErrorCode(error);
  return code && KNOWN.has(code) ? `timesheet.errors.${code}` : 'timesheet.errors.generic';
}
